import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, RotateCcw, ArrowLeft } from 'lucide-react';
import { StatusCards } from '@/components/admin/search-indexing/StatusCards';
import { QueueTable } from '@/components/admin/search-indexing/QueueTable';
import { IndexingLog } from '@/components/admin/search-indexing/IndexingLog';
import { StatisticsCharts } from '@/components/admin/search-indexing/StatisticsCharts';
import { TestIndexingForm } from '@/components/admin/search-indexing/TestIndexingForm';
import { useSearchIndexingData } from '@/hooks/useSearchIndexingData';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const SearchIndexing = () => {
  const navigate = useNavigate();
  const {
    stats,
    queue,
    logs,
    successRates,
    dailyVolume,
    testIndexing,
    retryFailedJobs,
    isTestingMeta,
    isRetrying
  } = useSearchIndexingData();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Search Indexing - Admin Dashboard</title>
      </Helmet>

      <Navbar />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 space-y-6">
          <div className="mb-6">
            <Button 
              variant="ghost" 
              size="sm" 
              className="gap-1"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Search Engine Indexing</h1>
              <p className="text-muted-foreground">Monitor and manage Google & Bing indexing</p>
            </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                queue.refetch();
                logs.refetch();
                stats.refetch();
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => retryFailedJobs()}
              disabled={isRetrying || !stats.data?.failed_count}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Retry Failed
            </Button>
          </div>
        </div>

        <StatusCards stats={stats.data} />

        <StatisticsCharts 
          successRates={successRates.data} 
          dailyVolume={dailyVolume.data} 
        />

        <TestIndexingForm 
          onTest={testIndexing} 
          isLoading={isTestingMeta} 
        />

        <Tabs defaultValue="queue" className="w-full">
          <TabsList>
            <TabsTrigger value="queue">Queue ({queue.data?.length || 0})</TabsTrigger>
            <TabsTrigger value="logs">Logs ({logs.data?.length || 0})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="queue">
            <Card>
              <CardHeader>
                <CardTitle>Indexing Queue</CardTitle>
              </CardHeader>
              <CardContent>
                <QueueTable items={queue.data} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle>Recent Indexing Logs</CardTitle>
              </CardHeader>
              <CardContent>
                <IndexingLog logs={logs.data} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SearchIndexing;
